import React from "react";

import {
	type Money,
	addMoney,
	averageMoney,
	formatMoney,
	formatMoneyCompact,
	formatMoneyWhole,
	subtractMoney,
	toMajorFloatAmount,
	toMinorUnits,
	zeroMoney,
} from "@performyard/money";
import { Text } from "@performyard/py-components";

import { getColor } from "../../../../../utils/getColor";

import { BudgetDoughnutChart } from "../BudgetDoughnutChart";

type LegendItem = {
	label: string;
	subtitle: string;
	/** Exact amount, for the text label. */
	value: Money;
	/**
	 * The same amount as a plain number, for the doughnut's arc geometry.
	 *
	 * A genuine float boundary: a chart slice is a proportion of a pixel arc, so
	 * sub-cent precision is meaningless there. The displayed dollar figure comes from
	 * `value` above, never from this.
	 */
	chartValue: number;
	color: string;
};

type TotalBudgetBreakdownProps = {
	approvedSpend: Money;
	submittedSpend: Money;
	approvedBudgetCount: number;
	submittedBudgetCount: number;
	notSubmittedEmployeeCount: number;
	employeesApproved: number;
	employeesSubmitted: number;
	notApprovedSpend: Money;
};

// This widget shows "$2M" for round magnitudes but "$1.25M" for fractional ones.
const formatCurrency = (value: Money): string => formatMoneyCompact(value, { trimWholeFractions: true });

// Exact to the penny so it reconciles against the per-team budgets.
const formatFullCurrency = (value: Money): string => formatMoney(value);

const pluralize = (count: number, singular: string): string => (count === 1 ? singular : `${singular}s`);

const formatAvgPerEmployee = (spend: Money, count: number): string => {
	if (count === 0) return formatMoneyWhole(zeroMoney());
	// Remainder-safe average rather than a rounded float division.
	return formatMoneyWhole(averageMoney(spend, count));
};

export const TotalBudgetBreakdown: React.FC<TotalBudgetBreakdownProps> = ({
	approvedSpend,
	submittedSpend,
	approvedBudgetCount,
	submittedBudgetCount,
	notSubmittedEmployeeCount,
	employeesApproved,
	employeesSubmitted,
	notApprovedSpend,
}) => {
	// Subtracted as money, clamped at zero on exact minor units. These are cycle-wide
	// totals, so a float subtraction here made the legend rows disagree with the total
	// printed directly above them.
	const notSubmittedSpendRaw = subtractMoney(notApprovedSpend, submittedSpend);
	const notSubmittedSpend = toMinorUnits(notSubmittedSpendRaw) > 0 ? notSubmittedSpendRaw : zeroMoney();
	const totalBudget = addMoney(notApprovedSpend, approvedSpend);

	const approvedColor = getColor("green", 5);
	const submittedColor = getColor("yellow", 2);
	const notSubmittedColor = getColor("neutral", 2);

	const legendItems: LegendItem[] = [
		{
			label: "Approved Spend",
			subtitle: `${formatAvgPerEmployee(approvedSpend, employeesApproved)} avg per employee \u2022 ${approvedBudgetCount} ${pluralize(approvedBudgetCount, "budget")} approved`,
			value: approvedSpend,
			chartValue: toMajorFloatAmount(approvedSpend),
			color: approvedColor,
		},
		{
			label: "Submitted Spend",
			subtitle: `${formatAvgPerEmployee(submittedSpend, employeesSubmitted)} avg per employee \u2022 ${submittedBudgetCount} ${pluralize(submittedBudgetCount, "budget")} submitted`,
			value: submittedSpend,
			chartValue: toMajorFloatAmount(submittedSpend),
			color: submittedColor,
		},
		{
			label: "Not Submitted",
			subtitle: `Not yet assigned \u2022 ${notSubmittedEmployeeCount.toLocaleString("en-US")} ${pluralize(notSubmittedEmployeeCount, "employee")}`,
			value: notSubmittedSpend,
			chartValue: toMajorFloatAmount(notSubmittedSpend),
			color: notSubmittedColor,
		},
	];

	return (
		<div className="box-border flex h-[296px] w-1/2 min-w-0 flex-col rounded-lg border border-solid border-neutral-2 bg-neutral-0 p-4">
			<Text size="paragraph-3" weight="semi-bold">
				Total Budget Breakdown
			</Text>
			<div className="mt-4 flex flex-1 items-center gap-8">
				<BudgetDoughnutChart data={legendItems} />
				<div className="flex flex-1 flex-col">
					{legendItems.map((item) => (
						<div key={item.label} className="flex items-start gap-3 py-2">
							<div
								className="mt-1.5 h-3 w-3 shrink-0 rounded-full"
								style={{ backgroundColor: item.color }}
							/>
							<div className="flex-1">
								<Text color="neutral-9" size="caption" weight="semi-bold">
									{item.label}
								</Text>
								<Text color="neutral-9" size="caption" weight="regular">
									{item.subtitle}
								</Text>
							</div>
							<Text color="neutral-9" size="paragraph-3" weight="semi-bold">
								{formatCurrency(item.value)}
							</Text>
						</div>
					))}
					<div className="mt-2 flex items-center border-t border-solid border-neutral-2 pt-2">
						<Text size="paragraph-3" weight="semi-bold">
							Total Budget
						</Text>
						<div className="ml-auto">
							<Text size="paragraph-3" weight="bold">
								{formatFullCurrency(totalBudget)}
							</Text>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};
